<template>
  <div class="flex flex-col p-3.5 gap-y-2">
    <BaseDetailsPreviewEditHeader
      title=""
      link-name="salespersons-management"
      hide-layout-options
      :mode="mode"
      :is-modified="isModified"
      is-edit-active
      @edit-mode="mode = 'edit'"
      @cancel="onCancelEditMode"
      @save="onSaveChanges"></BaseDetailsPreviewEditHeader>
    <SingleSelect
      v-model="selectedCompanySite"
      :options="companySites"
      :placeholder="$t('salesperson.selectSite')"
      item-text="name"
      class="w-full h-8 text-xs md:w-2/3 lg:w-1/3"
      @on-change="onCompanySiteChange"></SingleSelect>

    <table>
      <template v-for="header in salesPersonsHeaders" :key="header.value">
        <th
          scope="col"
          class="px-3 py-2 text-xs font-normal text-left text-gray-400 uppercase border border-b-0 cursor-pointer">
          {{ header.text }}
        </th>
      </template>
      <tbody class="bg-white border-t divide-y divide-gray-200">
        <tr class="select-none">
          <template v-for="i in 2" :key="i">
            <td class="p-0 align-top cursor-pointer" :class="i > 1 ? 'border-r ' : 'border-l'">
              <span v-if="i == 1">
                <tree-list
                  v-model="nodes"
                  :has-delete-option="false"
                  :has-edit-option="false"
                  :has-add-option="false"
                  :inline-editing="false"
                  is-main-tree
                  :mode="mode"
                  table
                  class="border-t-0" />
              </span>
              <div v-else>
                <!-- name column -->
                <table class="w-full border-separate">
                  <tbody class="align-top divide-gray-200">
                    <tr v-for="j in visibleNodes" :key="j.uid" class="">
                      <td class="px-2 text-sm font-medium align-top border-b select-none">
                        <div class="h-[32px] flex justify-start items-center">
                          {{ getNameById(j.uid) }}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </td>
          </template>
        </tr>
      </tbody>
    </table>
  </div>
  <BaseDrawerConfirm
    :is-active="confirmModalActive"
    @cancel="confirmModalActive = false"
    @confirm="onDiscardChanges" />
</template>
<script setup lang="ts">
import { onMounted, ref, computed, watchEffect } from 'vue';
import { getCompanySites } from '@/services/api/companies';
import { addOrEditSalesperson, removeSalesPersonsByIds } from '@/services/api/user';
import SingleSelect from '@/components/common/SingleSelect.vue';
import TreeList from '@/components/common/TreeList.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import { v4 as uuidv4 } from 'uuid';

import type { CompanySite } from '@/types/Companies';
import type { SalesPerson } from '@/types/SalesPersons';
import type { TreeType } from '@/types/ComponentTypes';

import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import { useToastStore } from '@/stores/toast';
import { useRouter } from 'vue-router';
import { deepCopy } from '@/utils/data';
import { useI18n } from 'vue-i18n';

const toastStore = useToastStore();
const { t: $t } = useI18n({ useScope: 'global' });

const { salespersons } = storeToRefs(useLookupsStore());

const { getSalesPersonsLookup } = useLookupsStore();

const selectedCompanySite = ref<CompanySite>();
const companySites = ref<CompanySite[]>([]);
const salesPersons = ref<SalesPerson[]>([]);
const router = useRouter();

let nodes = ref<TreeType[]>([]);

const salesPersonsHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
];

const mode = ref<'edit' | 'view'>('view');

const originalData = ref<SalesPerson[]>([]);
const originalNodes = ref<TreeType[]>([]);
const isModified = ref<boolean>(false);

async function loadDataCreateTree() {
  await getSalesPersonsLookup();
  salesPersons.value = deepCopy<SalesPerson[]>(
    salespersons.value.filter((x) => x.companySiteID == selectedCompanySite.value?.id)
  );

  if (salesPersons.value) nodes.value = createTreeListNodes(salesPersons.value);
  originalNodes.value = deepCopy<TreeType[]>(nodes.value);
  originalData.value = deepCopy<SalesPerson[]>(salesPersons.value);
  nodesStructure.value = deepCopy<TreeType[]>(nodes.value);
}
onMounted(async () => {
  companySites.value = await getCompanySites();
  selectedCompanySite.value = companySites.value[0];
  await loadDataCreateTree();
});

function createTreeListNodes(
  items: SalesPerson[],
  parentId?: string,
  level: number = 1,
  rootCode: string = ''
): TreeType[] {
  const children = items.filter((item) => item.salesPersonParentID === parentId);
  return children.map((item, index) => {
    const sequence = index;

    const currentRootCode = rootCode ? `${rootCode}.${sequence}` : `${sequence}`;

    return {
      uid: item.id,
      label: item.code,
      expanded: false,
      parentId: item.salesPersonParentID ? item.salesPersonParentID : undefined,
      sequence: sequence,
      level,
      rootCode: currentRootCode,
      children: createTreeListNodes(items, item.id, level + 1, currentRootCode),
    };
  });
}

const visibleNodes = computed(() => {
  if (!nodes.value) return [];
  let visibleNodesArray: TreeType[] = [];
  for (const rootNode of nodes.value) {
    visibleNodesArray = visibleNodesArray.concat(getVisibleNodes(rootNode));
  }
  return visibleNodesArray;
});

//find and store expanded nodes
function getVisibleNodes(node: TreeType): TreeType[] {
  //add root node
  const visibleNodesArray: TreeType[] = [node];

  //add children
  if (node.expanded && node.children) {
    for (const child of node.children) {
      visibleNodesArray.push(...getVisibleNodes(child));
    }
  }

  return visibleNodesArray;
}

async function onCompanySiteChange(e: any) {
  if (e.id != selectedCompanySite.value?.id) {
    selectedCompanySite.value = e;
    await loadDataCreateTree();
  }
}

function getNameById(id: string) {
  const result = salesPersons.value.find((x) => x.id == id);
  if (result) return result.name;
  return '-';
}

//Tree List Actions
const idsToDelete = ref<string[]>([]);
const nodesStructure = ref<TreeType[]>([]);
const isBackMode = ref(false);

function onCancelEditMode(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
  } else {
    router.push('/salespersons-management');
  }
  mode.value = 'view';
}

const confirmModalActive = ref(false);

async function onDiscardChanges() {
  confirmModalActive.value = false;
  mode.value = 'view';
  await loadDataCreateTree();
  if (isBackMode.value) {
    router.push('/salespersons-management');
  }
}

watchEffect(() => {
  isModified.value =
    JSON.stringify(nodes.value) !== JSON.stringify(originalNodes.value) ||
    JSON.stringify(salesPersons.value) !== JSON.stringify(originalData.value);
});

async function onSaveChanges() {
  mode.value = 'view';

  updateNodes(nodes.value);
  const flatNodes = flattenAllNodes(nodes.value);

  salesPersons.value = salesPersons.value.filter((item: SalesPerson) => {
    const match = flatNodes.find((node) => node.uid === item.id);

    if (match) {
      item.rootCode = match.rootCode ? match.rootCode : '';
      item.sequence = match.sequence ? match.sequence : 1;
      item.salesPersonParentID = match.parentId || null;
      return true;
    }

    return false;
  });

  if (!isModified.value) return;

  for (const salesperson of salesPersons.value) {
    const response = await addOrEditSalesperson(salesperson);
    if (response.length)
      toastStore.error({
        timeout: 5000,
        text: `Failed to save salesperson ${salesperson.name}.
        User with the same username already exists.`,
      });
  }

  if (idsToDelete.value.length) {
    await removeSalesPersonsByIds(idsToDelete.value);
  }
  salespersons.value = [];
  await loadDataCreateTree();
}

function flattenNodes(node: TreeType) {
  let flat: TreeType[] = [];

  const traverse = (currentNode: TreeType, parentNode?: TreeType) => {
    flat.push(currentNode);
    const salesperson = salesPersons.value.find((x) => x.id == currentNode.uid);
    if (salesperson) {
      salesperson.salesPersonParentID = parentNode ? parentNode.uid : undefined;
      if (!salesperson.userID) salesperson.userID = uuidv4();
    }
    if (currentNode.children && currentNode.children.length) {
      currentNode.children.forEach((child) => traverse(child, currentNode));
    }
  };
  traverse(node);
  return flat;
}

function flattenAllNodes(nodesArray: TreeType[]) {
  let allFlatNodes: TreeType[] = [];

  nodesArray.forEach((node) => {
    const flatNodes = flattenNodes(node);
    allFlatNodes = allFlatNodes.concat(flatNodes);
  });

  return allFlatNodes;
}

function updateNodes(
  nodes: TreeType[],
  parentId: string | undefined = undefined,
  parentRootCode = '',
  parentLevel = 0
) {
  nodes.forEach((node, index) => {
    node.sequence = index;

    node.level = parentLevel + 1;
    node.parentId = parentId;

    if (parentLevel === 0) {
      node.rootCode = `${node.sequence}`;
    } else {
      node.rootCode = `${parentRootCode}.${node.sequence}`;
    }

    if (node.children && node.children.length > 0) {
      updateNodes(node.children, node.uid, node.rootCode, node.level);
    }
  });
}
</script>
